/**
 * MilestoneActions - Renders workflow action buttons for a milestone
 *
 * Shows different actions based on milestone state and user role:
 *   - Developer/Consultant: "Submit Work" when MilestoneInProgress
 *   - Client: "Accept Delivery" / "Reject Delivery" when WaitingClientAcceptSubmission
 *
 * This component is designed to be embedded within MilestoneCard or
 * MilestoneList to provide inline workflow actions.
 */

import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input } from '@components/ui';
import {
  useSubmitMilestone,
  useAcceptMilestone,
  useRejectMilestone,
} from '@hooks/useMilestones';
import { useDisputesByMilestone } from '@hooks/useDisputes';
import { DisputeStatusBadge } from '@components/features/disputes/DisputeStatusBadge';
import { OpenDisputeModal } from '@components/features/disputes/OpenDisputeModal';
import {
  flowMilestoneState,
  MilestoneState,
} from '@lib/flowStates';
import { budgetPlanckToHuman } from '@lib/dusdUnits';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import type { Milestone, User, Project } from '@/types/index';

interface MilestoneActionsProps {
  /** The milestone to show actions for. */
  milestone: Milestone;
  /** The project ID this milestone belongs to. */
  projectId: string;
  /** The authenticated user. */
  user: User;
  /** Whether the user is a client for this project. */
  isClient: boolean;
  /** Whether the user is the consultant for this project. */
  isConsultant: boolean;
}

export function MilestoneActions({
  milestone,
  projectId,
  user,
  isClient,
  isConsultant,
}: MilestoneActionsProps) {
  const state = flowMilestoneState(milestone);
  const milestoneId = milestone.id;
  const [isDisputeModalOpen, setIsDisputeModalOpen] = useState(false);

  const { data: disputes = [] } = useDisputesByMilestone(projectId, milestoneId);
  const activeDispute = disputes[0];

  // If no milestone ID, it is a draft - no actions available
  if (!milestoneId) {
    return null;
  }

  const projectStub: Project = {
    id: projectId,
    title: `Project ${projectId.substring(0, 8)}`,
    clientId: isClient ? 'client' : '',
    consultantId: isConsultant ? 'consultant' : '',
    milestones: [milestone],
    objectives: [],
    constraints: [],
  };

  const isRejected = state === MilestoneState.SubmissionRejectedByClient;

  return (
    <div className="mt-3 pl-6">
      {/* Consultant/Developer: Submit work for review (never shown to clients) */}
      {!isClient &&
        (isConsultant ||
          !milestone.developerId ||
          Boolean(user.developerId && String(user.developerId) === String(milestone.developerId))) &&
        state === MilestoneState.MilestoneInProgress && (
          <SubmitWorkAction projectId={projectId} milestoneId={milestoneId} milestone={milestone} />
        )}

      {/* Client: Accept or reject submitted work */}
      {isClient && state === MilestoneState.WaitingClientAcceptSubmission && (
        <AcceptRejectActions projectId={projectId} milestoneId={milestoneId} />
      )}

      {/* Client: Milestone completed notice */}
      {isClient && state === MilestoneState.MilestoneCompleted && (
        <div className="flex items-center gap-2 text-xs text-green-400">
          <i className="ri-checkbox-circle-line" />
          <span>Milestone completed. Awaiting payment release.</span>
        </div>
      )}

      {/* Rejected milestone handling (DevStory 4.5 Criterios 2, 4, 5) */}
      {isRejected && (
        <div className="space-y-3">
          {activeDispute ? (
            /* Existing dispute card */
            <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-amber-300 flex items-center gap-1.5">
                  <i className="ri-scales-3-line" /> Formal Dispute Active
                </span>
                <DisputeStatusBadge status={activeDispute.status} size="sm" />
              </div>
              <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                Case #{activeDispute.id.substring(0, 8)} under DAO arbitration.
              </p>
              <Link
                to={`/projects/${projectId}/milestones/${milestoneId}/disputes/${activeDispute.id}`}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--state-brand-active,#36d399)] hover:underline"
              >
                <i className="ri-eye-line" /> View Dispute Case File
              </Link>
            </div>
          ) : (
            /* Rejection notice with Open Dispute button for Client & Consultant */
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border border-rose-500/20 bg-rose-500/5">
                <div className="flex items-center gap-1.5 text-xs text-rose-300">
                  <i className="ri-error-warning-line shrink-0" />
                  <span>Submission rejected by client.</span>
                </div>
                {(isClient || isConsultant) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDisputeModalOpen(true)}
                    className="text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10 shrink-0 self-start sm:self-auto"
                  >
                    <i className="ri-scales-3-line mr-1" />
                    Open Dispute
                  </Button>
                )}
              </div>

              {!isClient && isConsultant && (
                <SubmitWorkAction projectId={projectId} milestoneId={milestoneId} milestone={milestone} />
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal for opening a dispute */}
      <OpenDisputeModal
        isOpen={isDisputeModalOpen}
        onClose={() => setIsDisputeModalOpen(false)}
        project={projectStub}
        milestone={milestone}
        userRole={isClient ? 'client' : 'consultant'}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Submit work form for the consultant/developer.
 * Matches the Figma design with milestone summary, documentation, and links fields.
 */
function SubmitWorkAction({
  projectId,
  milestoneId,
  milestone,
}: {
  projectId: string;
  milestoneId: string;
  milestone: Milestone;
}) {
  const { workerRole, currency } = useDaoConfig();
  const [showForm, setShowForm] = useState(false);
  const [documentation, setDocumentation] = useState(milestone.documentation ?? '');
  const [links, setLinks] = useState(milestone.links ?? '');
  const submitMutation = useSubmitMilestone();

  const handleSubmit = useCallback(() => {
    submitMutation.mutate({
      projectId,
      milestoneId,
      documentation: documentation.trim() || undefined,
      links: links.trim() || undefined,
    });
  }, [submitMutation, projectId, milestoneId, documentation, links]);

  if (!showForm) {
    return (
      <Button
        variant="primary"
        size="sm"
        onClick={() => setShowForm(true)}
        className="text-xs"
      >
        <i className="ri-upload-2-line mr-1" />
        Submit for Review
      </Button>
    );
  }

  const budgetDisplay =
    milestone.budget !== undefined && milestone.budget !== null
      ? `${budgetPlanckToHuman(milestone.budget).toLocaleString('en-US')} ${currency.symbol}`
      : 'Budget Pending';

  return (
    <div className="space-y-4 p-4 rounded-lg border border-[#3D3D3D] bg-[#231F1F]">
      {/* Milestone summary */}
      <div>
        <h4 className="text-sm font-semibold text-[#F5F5F5] mb-3">
          Your Submission
        </h4>
        <div className="rounded-lg border border-[#3D3D3D] bg-[#1A1A1A] p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#F5F5F5]">
              {milestone.title}
            </span>
            <span className="text-xs font-medium text-[#36D399]">
              {budgetDisplay}
            </span>
          </div>
          {milestone.description && (
            <p className="text-xs text-[#9B9B9B] leading-relaxed">
              {milestone.description}
            </p>
          )}
          {milestone.developer && (
            <div className="flex items-center gap-2 text-xs text-[#9B9B9B]">
              <img
                className="h-5 w-5 rounded-full object-cover"
                src={`/developers/${milestone.developerId}/attachment`}
                alt={milestone.developer.name ?? workerRole}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/images/none.png';
                }}
              />
              <span>{milestone.developer.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Documentation field */}
      <div>
        <label className="block text-xs font-medium text-[#9B9B9B] mb-1.5">
          Documentation
        </label>
        <Input
          value={documentation}
          onChange={(e) => setDocumentation(e.target.value)}
          placeholder="Provide a link to your documentation"
        />
      </div>

      {/* Other Links field */}
      <div>
        <label className="block text-xs font-medium text-[#9B9B9B] mb-1.5">
          Other Links
        </label>
        <Input
          value={links}
          onChange={(e) => setLinks(e.target.value)}
          placeholder="Add links to supporting documents"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          variant="primary"
          onClick={handleSubmit}
          isLoading={submitMutation.isPending}
          disabled={submitMutation.isPending}
          className="flex-1"
        >
          Submit Milestone
        </Button>
        <Button
          variant="ghost"
          onClick={() => setShowForm(false)}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>

      {submitMutation.error && (
        <p className="text-xs text-red-400">{submitMutation.error.message}</p>
      )}
    </div>
  );
}

/**
 * Accept/Reject actions for the client.
 */
function AcceptRejectActions({
  projectId,
  milestoneId,
}: {
  projectId: string;
  milestoneId: string;
}) {
  const [action, setAction] = useState<'accept' | 'reject' | null>(null);
  const [comment, setComment] = useState('');

  const acceptMutation = useAcceptMilestone();
  const rejectMutation = useRejectMilestone();

  const handleAccept = useCallback(() => {
    acceptMutation.mutate({ projectId, milestoneId, comment });
  }, [acceptMutation, projectId, milestoneId, comment]);

  const handleReject = useCallback(() => {
    rejectMutation.mutate({ projectId, milestoneId, comment });
  }, [rejectMutation, projectId, milestoneId, comment]);

  if (action === null) {
    return (
      <div className="flex gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={() => setAction('accept')}
          className="text-xs"
        >
          <i className="ri-check-line mr-1" />
          Accept Delivery
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setAction('reject')}
          className="text-xs"
        >
          <i className="ri-close-line mr-1" />
          Reject Delivery
        </Button>
      </div>
    );
  }

  const isAccepting = action === 'accept';
  const mutation = isAccepting ? acceptMutation : rejectMutation;

  return (
    <div className="space-y-2 p-3 rounded-lg border border-[#3D3D3D] bg-[#231F1F]">
      <p className="text-xs text-[#9B9B9B]">
        {isAccepting
          ? 'Are you sure you want to accept this delivery? The milestone will be marked as completed.'
          : 'Are you sure you want to reject this delivery? The developer will need to address the feedback.'}
      </p>
      <Input
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={
          isAccepting
            ? 'Optional: Add a comment'
            : 'Required: State reasons for rejection *'
        }
      />
      <div className="flex gap-2">
        <Button
          variant={isAccepting ? 'primary' : 'destructive'}
          size="sm"
          onClick={isAccepting ? handleAccept : handleReject}
          isLoading={mutation.isPending}
          disabled={mutation.isPending || (!isAccepting && !comment.trim())}
          className="text-xs"
        >
          {isAccepting ? 'Confirm Accept' : 'Confirm Reject'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setAction(null);
            setComment('');
          }}
          className="text-xs"
        >
          Cancel
        </Button>
      </div>
      {mutation.error && (
        <p className="text-xs text-red-400">{mutation.error.message}</p>
      )}
    </div>
  );
}
